// 지표 달성 계산기 — 라벨(지표)의 목표·달성수치 수식을 입력해 지표 달성률을 계산한다 (기관 관리자 전용)
// $1, $2 … 는 핵심 과제 현황에서 그 라벨 묶음에 보이는 세부실행과제 순서다.
// 수식은 워크스페이스 설정(meta)에 저장돼 DB 구조 변경 없이 되돌릴 수 있다.
// 화면 모양(여백·섹션 제목·정보 줄)은 세부실행과제 상세 패널(TaskDetailPanel)과 맞춘다.

import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { X } from 'lucide-react'
import SidePanel from '@/components/ui/SidePanel'
import Button from '@/components/ui/Button'
import { useAlert } from '@/components/ui/Alert/exports'
import { handleApiError } from '@/utils/api-error-handler'
import { designTokens } from '@/utils/design-tokens'
import { TaskSummaryService } from '@/api/generated/services/TaskSummaryService'
import type { KeyTaskLabelStat } from '@/api/generated/models/KeyTaskLabelStat'

const font = designTokens.typography.fontFamily.sans[0]
const textColor = designTokens.colors.gray[6]

/**
 * 수식 계산 (백엔드 FormulaEvaluator 와 같은 규칙: 숫자·$n·+ - * /·괄호).
 * 비어 있으면 합계. 오류면 error 를 돌려준다 — 미리보기용이고 저장 후 값은 서버 계산을 쓴다.
 */
export const evaluateFormula = (
  formula: string,
  values: number[]
): { value: number | null; error: string | null } => {
  const text = formula.trim()
  if (!text) return { value: values.reduce((sum, v) => sum + v, 0), error: null }
  if (!/^[\d\s+\-*/().$]+$/.test(text)) return { value: null, error: '허용되지 않은 문자가 있습니다' }

  const tokens = text.match(/\$\d+|\d*\.?\d+|[+\-*/()]/g) ?? []
  let pos = 0
  const fail = (message: string): never => {
    throw new Error(message)
  }
  const parsePrimary = (): number => {
    const token = tokens[pos++]
    if (token === undefined) return fail('수식이 끝나지 않았습니다')
    if (token === '(') {
      const value = parseExpression()
      if (tokens[pos++] !== ')') fail('괄호가 올바르게 닫히지 않았습니다')
      return value
    }
    if (token === '-') return -parsePrimary()
    if (token === '+') return parsePrimary()
    if (token.startsWith('$')) {
      const index = Number(token.slice(1))
      if (index < 1 || index > values.length) fail(`참조된 변수가 존재하지 않습니다: ${token}`)
      return values[index - 1]
    }
    if (/^\d*\.?\d+$/.test(token)) return Number(token)
    return fail('공식 문법 오류')
  }
  const parseTerm = (): number => {
    let value = parsePrimary()
    while (tokens[pos] === '*' || tokens[pos] === '/') {
      const op = tokens[pos++]
      const right = parsePrimary()
      if (op === '/' && right === 0) fail('0으로 나눌 수 없습니다')
      value = op === '*' ? value * right : value / right
    }
    return value
  }
  const parseExpression = (): number => {
    let value = parseTerm()
    while (tokens[pos] === '+' || tokens[pos] === '-') {
      const op = tokens[pos++]
      const right = parseTerm()
      value = op === '+' ? value + right : value - right
    }
    return value
  }

  try {
    const value = parseExpression()
    if (pos < tokens.length) fail('공식 문법 오류')
    return { value, error: null }
  } catch (err) {
    return { value: null, error: err instanceof Error ? err.message : '공식 문법 오류' }
  }
}

const formatFigure = (value: number | null): string =>
  value == null ? '—' : value.toLocaleString('ko-KR', { maximumFractionDigits: 4 })

// ----- 세부실행과제 상세 패널과 같은 모양의 섹션 제목·정보 줄 -----

const SectionHeader = ({ title }: { title: string }) => (
  <div
    className="pb-2 border-b flex items-center justify-between"
    style={{ borderBottomColor: designTokens.colors.gray[1], borderBottomWidth: '1px', padding: '7px 0px' }}
  >
    <h3 style={{ fontFamily: font, fontSize: '18px', fontWeight: 700, color: textColor }}>{title}</h3>
  </div>
)

const InfoField = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="flex items-center gap-5">
    <div className="w-37 flex-shrink-0">
      <span style={{ fontFamily: font, fontSize: '16px', fontWeight: 400, color: textColor }}>{label}</span>
    </div>
    <div className="flex-1 py-2">
      {typeof value === 'string' ? (
        <span style={{ fontFamily: font, fontSize: '16px', fontWeight: 400, color: textColor }}>{value}</span>
      ) : (
        value
      )}
    </div>
  </div>
)

const InfoText = ({ children, bold = false }: { children: ReactNode; bold?: boolean }) => (
  <span style={{ fontFamily: font, fontSize: '16px', fontWeight: bold ? 700 : 400, lineHeight: 1.6, color: textColor }}>
    {children}
  </span>
)

const codeStyle = {
  display: 'inline-block',
  padding: '6px 10px',
  borderRadius: 4,
  background: designTokens.colors.gray[0],
  fontFamily: 'monospace',
  fontSize: 14,
  color: textColor,
}

const VARIABLE_GRID = '64px minmax(0, 1fr) 90px 90px'

interface LabelFormulaPanelProps {
  isOpen: boolean
  onClose: () => void
  workspaceId: string
  stat: KeyTaskLabelStat | null
  onSaved: () => void
}

export default function LabelFormulaPanel({
  isOpen,
  onClose,
  workspaceId,
  stat,
  onSaved,
}: LabelFormulaPanelProps) {
  const { showError, showSuccess } = useAlert()
  const [targetFormula, setTargetFormula] = useState('')
  const [doneFormula, setDoneFormula] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    setTargetFormula(stat?.target_formula ?? '')
    setDoneFormula(stat?.done_formula ?? '')
  }, [isOpen, stat])

  const variables = useMemo(() => stat?.variables ?? [], [stat])
  const target = evaluateFormula(targetFormula, variables.map((v) => v.target_figure))
  const done = evaluateFormula(doneFormula, variables.map((v) => v.done_figure))
  const rate =
    target.value && done.value != null ? (done.value / target.value) * 100 : null

  const handleSave = async () => {
    if (!stat) return
    if (target.error || done.error) {
      showError('수식 오류를 먼저 고쳐 주세요.')
      return
    }
    setIsSaving(true)
    try {
      await TaskSummaryService.appsTaskViewsUpdateKeyTaskLabelFormula({
        requestBody: {
          workspace_id: workspaceId,
          label_id: stat.label_id,
          target_formula: targetFormula.trim() || null,
          done_formula: doneFormula.trim() || null,
        },
      })
      showSuccess('계산기 수식을 저장했습니다.')
      onSaved()
      onClose()
    } catch (error) {
      handleApiError(error, showError, '계산기 수식을 저장하지 못했습니다.')
    } finally {
      setIsSaving(false)
    }
  }

  const headerActions = (
    <>
      <Button size="small" color="white" onClick={onClose} disabled={isSaving}>
        취소
      </Button>
      <Button size="small" color="dark" onClick={handleSave} disabled={isSaving || !stat}>
        {isSaving ? '저장 중...' : '저장'}
      </Button>
    </>
  )

  const formulaInput = (
    label: string,
    value: string,
    onChange: (value: string) => void,
    error: string | null
  ) => (
    <div className="flex flex-col gap-1">
      <div style={{ position: 'relative' }}>
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="비워두면 합계로 계산합니다"
          className="w-full h-10 border-2 rounded"
          style={{
            padding: '0 40px 0 12px',
            borderColor: error ? designTokens.colors.error.DEFAULT : designTokens.colors.gray[1],
            fontFamily: font,
            fontSize: 16,
            color: textColor,
            outline: 'none',
          }}
        />
        {value && (
          <button
            type="button"
            onClick={() => onChange('')}
            aria-label={`${label} 지우기`}
            style={{ position: 'absolute', right: 10, top: 10, color: designTokens.colors.gray[3] }}
          >
            <X size={18} />
          </button>
        )}
      </div>
      {error && <span style={{ fontSize: 13, color: designTokens.colors.error.DEFAULT }}>{error}</span>}
    </div>
  )

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      title={`지표 달성 계산기 · ${stat?.label_key ?? ''}`}
      width={800}
      zIndex={60}
      headerActions={headerActions}
    >
      <div className="flex flex-col space-y-6" style={{ padding: '24px' }}>
        {/* 변수: $n = 라벨 묶음의 n번째 세부실행과제 */}
        <div className="space-y-2">
          <SectionHeader title="변수 (세부실행과제 성과)" />
          <div style={{ padding: '0px 4px' }}>
            {variables.length === 0 ? (
              <div className="py-2">
                <InfoText>이 라벨에 세부실행과제가 없습니다.</InfoText>
              </div>
            ) : (
              <div className="flex flex-col">
                <div
                  className="grid py-2"
                  style={{
                    gridTemplateColumns: VARIABLE_GRID,
                    gap: 12,
                    fontFamily: font,
                    fontSize: 14,
                    color: designTokens.colors.gray[5],
                    borderBottom: `1px solid ${designTokens.colors.gray[1]}`,
                  }}
                >
                  <span>변수</span>
                  <span>세부실행과제</span>
                  <span style={{ textAlign: 'right' }}>목표</span>
                  <span style={{ textAlign: 'right' }}>달성</span>
                </div>
                {variables.map((variable) => (
                  <div
                    key={variable.task_id}
                    className="grid items-center py-2"
                    style={{
                      gridTemplateColumns: VARIABLE_GRID,
                      gap: 12,
                      fontFamily: font,
                      fontSize: 16,
                      color: textColor,
                      borderBottom: `1px solid ${designTokens.colors.gray[0]}`,
                    }}
                  >
                    <span style={{ ...codeStyle, padding: '2px 8px', textAlign: 'center', fontWeight: 700 }}>
                      ${variable.index}
                    </span>
                    <span style={{ wordBreak: 'keep-all' }}>{variable.task_name}</span>
                    <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {formatFigure(variable.target_figure)}
                    </span>
                    <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                      {formatFigure(variable.done_figure)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* 수치 설정 */}
        <div className="space-y-2">
          <SectionHeader title="수치 설정" />
          <div className="space-y-2" style={{ padding: '0px 4px' }}>
            <InfoField
              label="목표수치 수식"
              value={formulaInput('목표수치 수식', targetFormula, setTargetFormula, target.error)}
            />
            <InfoField
              label="달성수치 수식"
              value={formulaInput('달성수치 수식', doneFormula, setDoneFormula, done.error)}
            />
            <InfoField label="목표수치" value={formatFigure(target.value)} />
            <InfoField label="달성수치" value={formatFigure(done.value)} />
            <InfoField
              label="지표 달성률"
              value={
                <span style={{ fontFamily: font, fontSize: 16, fontWeight: 700, color: textColor }}>
                  {rate == null ? '—' : `${rate.toFixed(1)}%`}
                </span>
              }
            />
          </div>
        </div>

        {/* 수식 입력 안내 */}
        <div className="space-y-2">
          <SectionHeader title="수식 입력 안내" />
          <div className="space-y-4" style={{ padding: '8px 4px 0' }}>
            <InfoText>목표수치 수식과 달성수치 수식은 아래 3가지 방법으로 입력할 수 있습니다.</InfoText>
            <div className="flex flex-col gap-2">
              <InfoText bold>1. 기본 계산 (공백)</InfoText>
              <InfoText>수식 입력란을 비워두면 세부실행과제 값의 합계(sum)로 자동 계산됩니다.</InfoText>
            </div>
            <div className="flex flex-col gap-2 items-start">
              <InfoText bold>2. 숫자 입력</InfoText>
              <InfoText>숫자만 입력하면 해당 값이 그대로 적용됩니다.</InfoText>
              <code style={codeStyle}>예: 400</code>
            </div>
            <div className="flex flex-col gap-2 items-start">
              <InfoText bold>3. 수식 입력</InfoText>
              <InfoText>
                위 변수 목록의 세부실행과제를 <code style={{ ...codeStyle, padding: '1px 6px' }}>$번호</code> 형태로
                참조하여 수식을 작성합니다. 목표수치 수식에는 목표 값, 달성수치 수식에는 달성 값이 들어갑니다.
              </InfoText>
              <code style={codeStyle}>예: $1 + 200 + $2 * 1.2</code>
              <code style={codeStyle}>예: ($1 + 10) * $2 + 30</code>
            </div>
            <div
              className="flex flex-col gap-1 rounded"
              style={{
                padding: '12px 16px',
                borderLeft: `4px solid ${designTokens.colors.primary.DEFAULT}`,
                background: designTokens.colors.gray[0],
              }}
            >
              <InfoText bold>입력 가능한 값</InfoText>
              <InfoText>숫자, 세부실행과제 참조($), 사칙연산자(+, -, *, /), 괄호( )</InfoText>
            </div>
          </div>
        </div>
      </div>
    </SidePanel>
  )
}
